/**
 * AddNoteForm Component
 *
 * Form for adding notes and responses to support tickets.
 * Supports both internal notes (staff only) and public responses (visible to customer).
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 2: Ticket Detail Page Components
 * Location: apps/web/src/components/support/AddNoteForm.jsx
 */

import React from 'react';
import { Lock, Eye, Send, X } from 'lucide-react';

export function AddNoteForm({
  content,
  noteType,
  onContentChange,
  onNoteTypeChange,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const handleSubmit = (  _e) => {
    _e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Note Type Toggle */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <span className="text-sm font-medium text-gray-700">Visibility:</span>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => onNoteTypeChange('internal')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
              noteType === 'internal'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Lock className="w-4 h-4" />
            Internal Note
          </button>
          <button
            type="button"
            onClick={() => onNoteTypeChange('public')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
              noteType === 'public'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4" />
            Public Response
          </button>
        </div>
      </div>

      {/* Note Type Description */}
      <div className={`p-3 rounded-lg border ${
        noteType === 'internal'
          ? 'bg-gray-50 border-gray-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-start gap-2">
          {noteType === 'internal' ? (
            <Lock className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Eye className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={`text-xs ${
            noteType === 'internal' ? 'text-gray-700' : 'text-blue-700'
          }`}>
            {noteType === 'internal'
              ? 'This note is only visible to support staff and will not be sent to the customer.'
              : 'This response will be visible to the customer and sent via email.'}
          </p>
        </div>
      </div>

      {/* Text Area */}
      <div>
        <textarea
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          placeholder={
            noteType === 'internal'
              ? 'Add an internal note for your team...'
              : 'Write a response to the customer...'
          }
          rows={6}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {content.length} characters
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !content.trim()}
          className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
            noteType === 'internal'
              ? 'bg-gray-600 hover:bg-gray-700'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          <Send className="w-4 h-4" />
          {submitting
            ? 'Sending...'
            : noteType === 'internal'
            ? 'Add Internal Note'
            : 'Send Response'}
        </button>
      </div>
    </form>
  );
}

export default AddNoteForm;
